import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from './Button'
import { useToast } from './ToastProvider'
import { getApiErrorMessage } from '../../api/apiError'

interface ExcelExportButtonProps {
  /**
   * Dışa aktarmayı yapan çağrı — ekranın `export<X>(aktifFiltreler)` api fonksiyonu (içinde
   * `downloadXlsx` vardır). Promise reddedilirse hata toast'ı bu bileşen basar.
   */
  onExport: () => Promise<void>
  /** Buton metni (varsayılan: ortak "Excel'e Aktar"). */
  label?: string
  /** Dışa aktarılacak satır yoksa buton pasif olur (boş dosya indirilmesin). */
  disabled?: boolean
}

/**
 * Paylaşılan **"Excel'e Aktar"** butonu (Reuse Mandate). Her liste ekranı yalnız kendi `export…` çağrısını
 * verir; yükleniyor durumu, hata toast'ı ve görünüm (`ui-toolbar-btn` + `pi-file-excel`) tek yerdedir:
 *
 * ```tsx
 * <ExcelExportButton onExport={() => exportEventTypes({ status, q })} disabled={rows.length === 0} />
 * ```
 *
 * Hata mesajı olarak backend'in **lokalize** metni ({@code getApiErrorMessage}) tercih edilir; yoksa genel
 * hata gösterilir (yetki yoksa uç 403 döner — buton görünürlüğü UI kolaylığıdır, asıl koruma backend'de).
 *
 * <p><b>Görünüm (2026-08-26):</b> "Yenile" ile AYNI araç çubuğu görünümü (`.ui-toolbar-btn`) —
 * kullanıcı isteği. Eskiden `outlined` idi ve yanındaki Yenile'den farklı duruyordu. Kural
 * `ui.css`'te tek yerde ve iki buton da onu okur.
 */
export default function ExcelExportButton({ onExport, label, disabled }: Readonly<ExcelExportButtonProps>) {
  const { t } = useTranslation()
  const toast = useToast()
  const [exporting, setExporting] = useState(false)

  const run = async () => {
    setExporting(true)
    try {
      await onExport()
    } catch (err) {
      toast.show({
        severity: 'error',
        summary: getApiErrorMessage(err) ?? t('common.errors.generic'),
        life: 3000,
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <Button
      type="button"
      text
      className="ui-toolbar-btn"
      icon="pi pi-file-excel"
      label={label ?? t('common.export')}
      loading={exporting}
      disabled={disabled}
      onClick={run}
    />
  )
}
